export interface MeinersPipelineCapacityInput {
  id?: string;
  name?: string | null;
  operator?: string | null;
  system_name?: string | null;
  source_capacity_mmcf_day?: number | null;
  source_capacity_scope?: string | null;
  capacity_basis?: string;
  capacity_mmcf_day?: number | string | null;
  diameter_inches?: number | string | null;
}

export interface MeinersPipelineCapacityFact {
  label: string;
  value: string;
  detail: string;
  sourceUrl?: string;
  sourceTitle?: string;
  systemKey?: string;
  capacityMmcfDay?: number | null;
  capacityBasis?: 'published' | 'estimated';
  status?: 'operating' | 'project';
}

const WHITEWATER_OPERATIONS_URL = 'https://www.whitewatermidstream.com/operations';
const MPLX_Q2_2026_URL = 'https://ir.mplx.com/CorporateProfile/press-releases/news-release/2026/MPLX-LP-Reports-Second-Quarter-2026-Financial-Results/default.aspx';
const KINDER_MORGAN_NATURAL_GAS_URL = 'https://www2.kindermorgan.com/Operations/Natural-Gas/Index';
const WTG_PROJECTS_URL = 'https://wtg-energy.com/infrastructure/work-in-action/';

function normalized(value: string | null | undefined): string {
  return (value || '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
}

function hasIdentity(value: string, expected: string): boolean {
  return value === expected || value.includes(expected);
}

function unconfirmed(detail: string, sourceUrl?: string, sourceTitle?: string): MeinersPipelineCapacityFact {
  return {
    label: 'Total pipeline capacity',
    value: 'Unconfirmed',
    detail,
    capacityMmcfDay: null,
    ...(sourceUrl && sourceTitle ? { sourceUrl, sourceTitle } : {}),
  };
}

/**
 * Curated public capacity facts for the pipelines in the Meiners Ranch briefing.
 * Input capacity fields are intentionally ignored: the general API model may contain
 * diameter screens or differently scoped values that are not system capacity.
 */
export function meinersPipelineCapacity(pl: MeinersPipelineCapacityInput): MeinersPipelineCapacityFact {
  const pipeline = `${normalized(pl.name)} ${normalized(pl.system_name)}`.trim();
  const operator = normalized(pl.operator);

  if (hasIdentity(pipeline, 'WHISTLER PIPELINE') && hasIdentity(operator, 'WWM OPERATING')) {
    return {
      label: 'Total pipeline capacity',
      value: '2.5 Bcf/d',
      detail: 'Operating Whistler mainline total capacity; confirm site delivery, pressure, and firm transport with WWM Operating.',
      sourceUrl: WHITEWATER_OPERATIONS_URL,
      sourceTitle: 'WhiteWater — Operations',
      systemKey: 'whistler',
      capacityMmcfDay: 2500,
      capacityBasis: 'published',
      status: 'operating',
    };
  }

  if (hasIdentity(pipeline, 'BLACKCOMB PIPELINE') && hasIdentity(operator, 'WWM OPERATING')) {
    return {
      label: 'Project design capacity',
      value: '2.5 Bcf/d',
      detail: 'Blackcomb project design capacity; commissioning began in July 2026, with in-service expected in Q4 2026.',
      sourceUrl: MPLX_Q2_2026_URL,
      sourceTitle: 'MPLX — Second-Quarter 2026 Results',
      systemKey: 'blackcomb',
      capacityMmcfDay: 2500,
      capacityBasis: 'published',
      status: 'project',
    };
  }

  if ((hasIdentity(pipeline, 'GCX') || hasIdentity(pipeline, 'GULF COAST EXPRESS'))
      && hasIdentity(operator, 'KINDER MORGAN TEXAS PIPELINE')) {
    return {
      label: 'Total pipeline capacity',
      value: '2.59 Bcf/d',
      detail: 'Operating GCX system total capacity; Kinder Morgan reports the system as fully subscribed under long-term binding agreements.',
      sourceUrl: KINDER_MORGAN_NATURAL_GAS_URL,
      sourceTitle: 'Kinder Morgan — Natural Gas Pipelines',
      systemKey: 'gcx',
      capacityMmcfDay: 2590,
      capacityBasis: 'published',
      status: 'operating',
    };
  }

  if ((hasIdentity(pipeline, 'EAGLE FORD MIDSTREAM') || hasIdentity(pipeline, 'EAGLEFORD MIDSTREAM'))
      && hasIdentity(operator, 'EAGLE FORD MIDSTREAM')) {
    return {
      label: 'Total pipeline capacity',
      value: '1.05 Bcf/d',
      detail: 'Operating Eagle Ford Midstream residue system total capacity; confirm capacity and delivery terms for the mapped Cotulla connection.',
      sourceUrl: KINDER_MORGAN_NATURAL_GAS_URL,
      sourceTitle: 'Kinder Morgan — Natural Gas Pipelines',
      systemKey: 'eagle-ford-midstream',
      capacityMmcfDay: 1050,
      capacityBasis: 'published',
      status: 'operating',
    };
  }

  if (hasIdentity(pipeline, 'CAMINO REAL') && hasIdentity(operator, 'WTG BEXAR TRANSMISSION')) {
    return unconfirmed(
      'Camino Real total capacity is unconfirmed in the reviewed operator materials; confirm capacity and delivery terms with WTG Bexar Transmission.',
      WTG_PROJECTS_URL,
      'WTG Energy — Work in Action',
    );
  }

  if (hasIdentity(pipeline, 'DOS CAMINOS') && hasIdentity(operator, 'DOS CAMINOS')) {
    return unconfirmed(
      'The published 500 MMcf/d figure applies to the Spears pipeline within the Dos Caminos network, not the mapped Cotulla segment; confirm this segment with the operator.',
      KINDER_MORGAN_NATURAL_GAS_URL,
      'Kinder Morgan — Natural Gas Pipelines',
    );
  }

  if (hasIdentity(pipeline, 'CARNERO') && hasIdentity(operator, 'CARNERO G P')) {
    return unconfirmed('Carnero pipeline total capacity was not confirmed in a public operator source; confirm capacity and delivery terms with Targa.');
  }

  if (hasIdentity(pipeline, 'TEXAS PIPELINE LLC') && hasIdentity(operator, 'TEXAS PIPELINE LLC')) {
    return unconfirmed('Texas Pipeline LLC total capacity was not confirmed in a public operator source; confirm capacity and delivery terms with the operator.');
  }

  return unconfirmed('Public total pipeline capacity was not confirmed for this system; verify capacity and delivery terms with the operator.');
}
